import React, { useCallback, useEffect, useRef } from 'react';
import { getAspectRatioValue } from 'utils/aspect-ratio';
import { useClipSelect } from '../../ClipSelectContext';
import ClipSelectVideoPlayer, {
  ClipSelectPlayerInstance,
} from '../../ClipSelectVideoPlayer';
import { useClipPlaybackManager } from '../ClipPlaybackManagerContext';

export type AudioClipSuggestionPreviewCardVideoProps = {
  videoId: number;
  className?: string;
};

const AudioClipSuggestionPreviewCardVideo: React.FC<AudioClipSuggestionPreviewCardVideoProps> = ({
  videoId,
  className,
}) => {
  const videoRef = useRef<ClipSelectPlayerInstance>(null);
  const {
    clipsPageInfo,
    isExporting,
    isGeneratingPreview,
    suggestion,
  } = useClipSelect({
    clipId: videoId,
  });
  const {
    startPlaying,
    stopPlaying,
    isClipPlaying: isPlaying,
    registerPlayer,
    unregisterPlayer,
  } = useClipPlaybackManager({
    clipId: videoId,
  });

  const videoUrl = suggestion?.videoUrl;
  const posterUrl = suggestion?.videoThumbnailUrl;
  const initialAspectRatio = getAspectRatioValue(
    clipsPageInfo?.aspectRatioName,
  );

  useEffect(() => {
    if (videoRef.current) {
      registerPlayer(videoRef.current);
    }

    return () => {
      unregisterPlayer();
    };
  }, [registerPlayer, unregisterPlayer]);

  // if the video is playing but the clip goes into a loading state, stop playback
  // since the overlay will prevent the user from clicking the pause button once
  // the loading state begins
  useEffect(() => {
    if (isExporting && isPlaying) {
      stopPlaying();
    }
  }, [isExporting, isGeneratingPreview, isPlaying, stopPlaying]);

  const handlePlaybackChange = useCallback(
    (playing: boolean) => {
      if (playing) {
        startPlaying();
      } else {
        stopPlaying();
      }
    },
    [startPlaying, stopPlaying],
  );

  return (
    <ClipSelectVideoPlayer
      ref={videoRef}
      initialAspectRatio={initialAspectRatio}
      posterUrl={posterUrl}
      videoUrl={videoUrl}
      className={className}
      isPlaying={isPlaying}
      onPlaybackChange={handlePlaybackChange}
    />
  );
};

export default AudioClipSuggestionPreviewCardVideo;
